import { useState, type JSX } from 'react';
import type { CreateInviteOptions, InviteInfo, LinkStatus } from '../../../net/api';
import type { RecorderStatus } from '../../../output/api';
import { Icon } from '../../components/Icon';
import { useActions, useProject, useRuntime, useUi, useUiContext } from '../../context';
import { linkStatusOf } from '../../actions/outputs';
import { safely } from '../../safe';
import '../panels.css';

export function streamButtonLabel(status: LinkStatus, busy: boolean): string {
  if (status === 'connecting') return 'Connecting… (cancel)';
  if (status === 'reconnecting') return 'Reconnecting… (stop)';
  if (status === 'live') return busy ? 'Stopping…' : 'Stop Streaming';
  return busy ? 'Starting…' : 'Start Streaming';
}

export function recordButtonLabel(status: RecorderStatus, busy: boolean): string {
  if (status === 'starting') return 'Starting…';
  if (status === 'stopping') return 'Stopping…';
  if (status === 'recording' || status === 'paused') return busy ? 'Stopping…' : 'Stop Recording';
  return busy ? 'Starting…' : 'Start Recording';
}

function CopyRow({ label, value }: { label: string; value: string }): JSX.Element {
  const { studio } = useUiContext();
  const copy = (): void =>
    void safely(studio, async () => {
      await navigator.clipboard.writeText(value);
      studio.notify('info', `${label} copied.`);
    }, 'Could not copy');
  return (
    <div>
      <div className="ui-dim">{label}</div>
      <div className="url-row">
        <input className="ui-input ui-mono" readOnly value={value} aria-label={label} onFocus={(e) => e.currentTarget.select()} />
        <button type="button" className="ui-icon-btn" aria-label={`Copy ${label}`} title="Copy" onClick={copy}>
          <Icon name="copy" />
        </button>
      </div>
    </div>
  );
}

/**
 * The guest controls of the studio API. Typed here, and looked up at run time, so the panel
 * keeps working against a kernel that does not offer one of them yet: such buttons are disabled.
 */
export interface GuestControlApi {
  admitGuest?(peerId: string): void;
  rejectGuest?(peerId: string, reason?: string): void;
  createInvite?(options: CreateInviteOptions): Promise<InviteInfo>;
  revokeInvite?(participantId: string): void;
  setGuestLinkEnabled?(enabled: boolean): Promise<string>;
  rotateGuestLink?(): Promise<string>;
  setAllowGuests?(allow: boolean): void | Promise<void>;
}

/** The studio's guest controls, as far as this kernel offers them. */
export function useGuestApi(): GuestControlApi {
  return useUiContext().studio as unknown as GuestControlApi;
}

export const UNAVAILABLE = 'Not available in this version of the studio';

const INVITE_LIFETIMES: readonly [number, string][] = [
  [3_600_000, '1 hour'],
  [24 * 3_600_000, '24 hours'],
  [7 * 24 * 3_600_000, '7 days'],
];

export function expiryLabel(expiresAt: number, now: number): string {
  const left = expiresAt - now;
  if (left <= 0) return 'expired';
  const hours = Math.floor(left / 3_600_000);
  if (hours >= 48) return `expires in ${Math.floor(hours / 24)} days`;
  if (hours >= 1) return `expires in ${hours} h`;
  return `expires in ${Math.max(1, Math.round(left / 60_000))} min`;
}

export function GuestAccess({ api }: { api: GuestControlApi }): JSX.Element {
  const { studio } = useUiContext();
  const broadcast = useRuntime((r) => r.broadcast);
  const [inviteName, setInviteName] = useState('');
  const [lifetime, setLifetime] = useState(INVITE_LIFETIMES[1]![0]);
  const [autoAdmit, setAutoAdmit] = useState(false);
  const now = Date.now();
  const sharedUrl = broadcast.room?.guestUrl ?? '';

  const invite = (): void =>
    void safely(studio, async () => {
      if (!api.createInvite) return;
      const created = await api.createInvite({ name: inviteName.trim() || undefined, expiresInMs: lifetime, autoAdmit });
      setInviteName('');
      try {
        await navigator.clipboard.writeText(created.url);
        studio.notify('info', 'Guest invitation created and copied.');
      } catch {
        studio.notify('info', 'Guest invitation created.');
      }
    }, 'Could not create the invitation');

  return (
    <div className="ui-col" style={{ gap: 6 }} aria-label="Guests">
      <label className="ui-check">
        <input
          type="checkbox"
          checked={broadcast.allowGuests}
          disabled={!api.setAllowGuests}
          title={api.setAllowGuests ? undefined : UNAVAILABLE}
          onChange={(e) => {
            const allow = e.target.checked;
            void safely(studio, async () => api.setAllowGuests?.(allow), 'Could not change guest access');
          }}
        />
        Accept guests
      </label>

      {broadcast.lobby.length > 0 ? (
        <div className="ui-col" style={{ gap: 4 }} role="list" aria-label="Waiting guests">
          <div className="ui-warn-text">Waiting to be let in ({broadcast.lobby.length})</div>
          {broadcast.lobby.map((entry) => (
            <div key={entry.peerId} className="peer-row" role="listitem">
              <Icon name="users" size={14} />
              <span className="ui-grow ui-ellipsis">{entry.name || 'Guest'}</span>
              <button
                type="button"
                className="ui-btn"
                disabled={!api.admitGuest}
                title={api.admitGuest ? undefined : UNAVAILABLE}
                aria-label={`Let ${entry.name || 'guest'} in`}
                onClick={() => void safely(studio, () => api.admitGuest?.(entry.peerId))}
              >
                <Icon name="check" size={14} /> Let in
              </button>
              <button
                type="button"
                className="ui-icon-btn"
                disabled={!api.rejectGuest}
                aria-label={`Turn ${entry.name || 'guest'} away`}
                title={api.rejectGuest ? 'Turn away (they may ask again)' : UNAVAILABLE}
                onClick={() => void safely(studio, () => api.rejectGuest?.(entry.peerId))}
              >
                <Icon name="close" size={14} />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div className="ui-dim">Invite a guest</div>
      <div className="url-row">
        <input
          className="ui-input"
          placeholder="Name (optional)"
          aria-label="Invited guest's name"
          value={inviteName}
          maxLength={64}
          onChange={(e) => setInviteName(e.target.value)}
        />
        <select className="ui-select" aria-label="Invitation valid for" value={lifetime} onChange={(e) => setLifetime(Number(e.target.value))}>
          {INVITE_LIFETIMES.map(([ms, label]) => (
            <option key={ms} value={ms}>
              {label}
            </option>
          ))}
        </select>
      </div>
      <div className="url-row">
        <label className="ui-check ui-grow" title="Without this, the guest waits until you let them in.">
          <input type="checkbox" checked={autoAdmit} onChange={(e) => setAutoAdmit(e.target.checked)} />
          Let in without asking
        </label>
        <button type="button" className="ui-btn" disabled={!api.createInvite || !broadcast.allowGuests} title={api.createInvite ? undefined : UNAVAILABLE} onClick={invite}>
          <Icon name="link" size={14} /> Create link
        </button>
      </div>

      {broadcast.invites.length > 0 ? (
        <div className="ui-col" style={{ gap: 4 }} role="list" aria-label="Invitations">
          {broadcast.invites.map((item) => (
            <div key={item.participantId} className="peer-row" role="listitem">
              <span className="ui-grow ui-ellipsis" title={item.url}>
                {item.name || `Guest ${item.participantId.slice(0, 4)}`}
                <span className="ui-dim"> — {item.revoked ? 'revoked' : expiryLabel(item.expiresAt, now)}</span>
              </span>
              <button
                type="button"
                className="ui-icon-btn"
                aria-label={`Copy invitation for ${item.name || 'guest'}`}
                title="Copy link"
                disabled={item.revoked}
                onClick={() =>
                  void safely(studio, async () => {
                    await navigator.clipboard.writeText(item.url);
                    studio.notify('info', 'Invitation copied.');
                  }, 'Could not copy')
                }
              >
                <Icon name="copy" size={14} />
              </button>
              <button
                type="button"
                className="ui-icon-btn"
                aria-label={`Revoke invitation for ${item.name || 'guest'}`}
                title={api.revokeInvite ? 'Revoke: the link stops working and this person cannot come back' : UNAVAILABLE}
                disabled={item.revoked || !api.revokeInvite}
                onClick={() => void safely(studio, () => api.revokeInvite?.(item.participantId))}
              >
                <Icon name="trash" size={14} />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <label className="ui-check" title="One link for everyone. Off is safer: a kicked person can come back through it until you replace it.">
        <input
          type="checkbox"
          checked={sharedUrl !== ''}
          disabled={!api.setGuestLinkEnabled}
          onChange={(e) => {
            const enabled = e.target.checked;
            void safely(studio, async () => api.setGuestLinkEnabled?.(enabled), 'Could not change the guest link');
          }}
        />
        Shared guest link
      </label>
      {sharedUrl !== '' ? (
        <>
          <CopyRow label="Guest link" value={sharedUrl} />
          <button
            type="button"
            className="ui-btn"
            disabled={!api.rotateGuestLink}
            title={api.rotateGuestLink ? 'The current link stops working at once' : UNAVAILABLE}
            onClick={() =>
              void safely(studio, async () => {
                await api.rotateGuestLink?.();
                studio.notify('info', 'The shared guest link was replaced; the old one no longer works.');
              }, 'Could not replace the guest link')
            }
          >
            <Icon name="refresh" size={14} /> Replace shared link
          </button>
        </>
      ) : null}
    </div>
  );
}

function BroadcastDetails(): JSX.Element | null {
  const { studio } = useUiContext();
  const broadcast = useRuntime((r) => r.broadcast);
  const guestApi = useGuestApi();
  if (broadcast.status === 'idle' && !broadcast.error) return null;
  return (
    <div className="controls-live" aria-label="Stream details">
      {broadcast.error ? (
        <div className="ui-error-text ui-selectable" role="alert">
          {broadcast.error}
        </div>
      ) : null}
      {broadcast.status === 'reconnecting' ? (
        <div className="ui-warn-text">Signalling connection lost; retrying (attempt {broadcast.reconnectAttempts}).</div>
      ) : null}
      {broadcast.room ? (
        <>
          <CopyRow label="Viewer link" value={broadcast.room.viewerUrl} />
          <GuestAccess api={guestApi} />
          <div className="ui-dim">
            {broadcast.peers.length === 0 ? 'Nobody connected yet.' : `Connected (${broadcast.peers.length})`}
          </div>
          {broadcast.peers.map((peer) => (
            <div key={peer.peerId} className="peer-row">
              <Icon name={peer.role === 'guest' ? 'users' : 'eye'} size={14} />
              <span className="ui-grow ui-ellipsis">{peer.name || peer.peerId.slice(0, 8)}</span>
              <span className="ui-dim">{peer.connectionState}</span>
              <button
                type="button"
                className="ui-icon-btn"
                aria-label={`Remove ${peer.name || peer.role}`}
                title={peer.role === 'guest' ? 'Remove: this guest cannot come back in this broadcast' : 'Disconnect'}
                onClick={() => void safely(studio, () => studio.kickPeer(peer.peerId))}
              >
                <Icon name="close" size={14} />
              </button>
            </div>
          ))}
        </>
      ) : null}
    </div>
  );
}

function WhipDetails(): JSX.Element | null {
  const whip = useRuntime((r) => r.whip);
  const endpoint = useProject((p) => p.output.streaming.whipEndpoint);
  if (whip.status === 'idle' && !whip.error) return null;
  return (
    <div className="controls-live" aria-label="WHIP details">
      <div>
        WHIP: <strong>{whip.status}</strong>
        {whip.status === 'reconnecting' ? ` (attempt ${whip.reconnectAttempts})` : ''}
      </div>
      <div className="ui-dim ui-ellipsis ui-selectable" title={endpoint}>
        {whip.resourceUrl ?? endpoint}
      </div>
      {whip.error ? (
        <div className="ui-error-text ui-selectable" role="alert">
          {whip.error}
        </div>
      ) : null}
    </div>
  );
}

function SwarmDetails(): JSX.Element | null {
  const swarm = useRuntime((r) => r.swarm);
  if (swarm.status === 'idle' && !swarm.error) return null;
  return (
    <div className="controls-live" aria-label="Swarm details">
      <div>
        Swarm: <strong>{swarm.status}</strong>
        {swarm.status === 'reconnecting' ? ` (attempt ${swarm.reconnectAttempts})` : ''}
        {swarm.status === 'live' ? ` · ${swarm.viewers} watching` : ''}
      </div>
      {swarm.streamId ? <div className="ui-dim ui-ellipsis ui-selectable">Stream {swarm.streamId}</div> : null}
      {swarm.hidesSeed && swarm.status !== 'idle' && !swarm.exposed ? <div className="ui-dim">Your address is hidden from viewers</div> : null}
      {swarm.privacyWarning ? (
        <div className="ui-error-text ui-selectable" role="alert">
          {swarm.privacyWarning}
        </div>
      ) : null}
      {swarm.error ? (
        <div className="ui-error-text ui-selectable" role="alert">
          {swarm.error}
        </div>
      ) : null}
    </div>
  );
}

export function ControlsPanel(): JSX.Element {
  const { ui } = useUiContext();
  const actions = useActions();
  const mode = useProject((p) => p.output.streaming.mode);
  const linkStatus = useRuntime((r) => linkStatusOf(mode, r));
  const recording = useRuntime((r) => r.recording.status);
  const recordingError = useRuntime((r) => r.recording.error);
  const studioMode = useRuntime((r) => r.studioMode);
  const ready = useRuntime((r) => r.ready);
  const busy = useUi((s) => s.busy);

  const streaming = linkStatus !== 'idle' && linkStatus !== 'error';
  const recordingActive = recording === 'recording' || recording === 'paused';
  const recordingPending = busy.recording || recording === 'starting' || recording === 'stopping';

  return (
    <div className="ui-panel">
      <div className="ui-panel-body controls">
        <button
          type="button"
          className={streaming ? 'ui-btn danger block' : 'ui-btn block'}
          disabled={!ready || busy.streaming}
          aria-busy={busy.streaming || linkStatus === 'connecting' || linkStatus === 'reconnecting'}
          onClick={() => actions.toggleStreaming()}
        >
          <Icon name="broadcast" /> {streamButtonLabel(linkStatus, busy.streaming)}
        </button>
        {mode === 'whip' ? <WhipDetails /> : mode === 'swarm' ? <SwarmDetails /> : <BroadcastDetails />}

        <button
          type="button"
          className={recordingActive ? 'ui-btn danger block' : 'ui-btn block'}
          disabled={!ready || recordingPending}
          aria-busy={recordingPending}
          onClick={() => actions.toggleRecording()}
        >
          <Icon name="record" /> {recordButtonLabel(recording, busy.recording)}
        </button>
        {recordingActive ? (
          <button type="button" className="ui-btn block" disabled={busy.recording} onClick={() => actions.togglePause()}>
            <Icon name={recording === 'paused' ? 'play' : 'pause'} /> {recording === 'paused' ? 'Resume Recording' : 'Pause Recording'}
          </button>
        ) : null}
        {recordingError ? (
          <div className="ui-error-text ui-selectable" role="alert">
            {recordingError}
          </div>
        ) : null}

        <button
          type="button"
          className={studioMode ? 'ui-btn active block' : 'ui-btn block'}
          aria-pressed={studioMode}
          onClick={() => actions.toggleStudioMode()}
        >
          <Icon name="split" /> Studio Mode
        </button>
        <button type="button" className="ui-btn block" onClick={() => ui.getState().openDialog({ type: 'settings' })}>
          <Icon name="gear" /> Settings
        </button>
        <button type="button" className="ui-btn block" disabled={!ready} onClick={() => actions.screenshot()}>
          <Icon name="snapshot" /> Screenshot
        </button>
      </div>
    </div>
  );
}
